import { useQuery } from '@tanstack/react-query'
import { useAccount } from 'wagmi'

export const BALANCER_POOL_ADDRESS = '0xba0163e18b8b6236d5046841e698f2f2d89bd4bd'

interface BalancerPoolResponse {
  data: {
    pool: {
      address: string
      name: string
      symbol: string
      dynamicData: {
        totalLiquidity: string
        totalShares: string
        aprItems: Array<{
          apr: number
          title: string
          type: string
        }>
      }
      userBalance: {
        totalBalance: string
        totalBalanceUsd: string
        walletBalance: string
        walletBalanceUsd: string
      } | null
    }
  }
}

interface UseBalancerPoolDataResult {
  tvl: string
  userDeposit: string
  apy: number
  isLoading: boolean
  error: Error | null
}

export function useBalancerPoolData(poolId: string): UseBalancerPoolDataResult {
  const { address: userAddress } = useAccount()

  const query = `
    query GetPool($id: String!, $chain: GqlChain!, $userAddress: String) {
      pool: poolGetPool(id: $id, chain: $chain, userAddress: $userAddress) {
        address
        name
        symbol
        dynamicData {
          totalLiquidity
          totalShares
          aprItems {
            apr
            title
            type
          }
        }
        userBalance {
          totalBalance
          totalBalanceUsd
          walletBalance
          walletBalanceUsd
        }
      }
    }
  `

  const { data, isLoading, error } = useQuery({
    queryKey: ['balancer-pool', poolId, userAddress],
    queryFn: async (): Promise<BalancerPoolResponse> => {
      const response = await fetch('https://api-v3.balancer.fi/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operationName: 'GetPool',
          variables: {
            id: poolId,
            chain: 'HYPEREVM',
            userAddress: userAddress || '',
          },
          query,
        }),
      })

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status}`)
      }

      const result = await response.json()
      if (result.errors) {
        throw new Error(`GraphQL errors: ${result.errors.map((e: any) => e.message).join(', ')}`)
      }

      return result
    },
    enabled: !!poolId,
  })

  // Parse the response data
  const poolData = data?.data?.pool
  const tvl = poolData?.dynamicData?.totalLiquidity || '0'
  const userBalance = poolData?.userBalance
  const userDeposit = userBalance?.walletBalanceUsd || '0'

  // Calculate average APY from APR items
  const aprItems = poolData?.dynamicData?.aprItems || []
  const totalApr = aprItems.reduce((sum, item) => sum + (item.apr || 0), 0)

  // Format currency values
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  })

  const tvlFormatted = formatter.format(Number.parseFloat(tvl))
  const userDepositFormatted = formatter.format(Number.parseFloat(userDeposit))

  return {
    tvl: tvlFormatted,
    userDeposit: userDepositFormatted,
    apy: totalApr,
    isLoading,
    error: error as Error | null,
  }
}
